import { useEffect, useRef, useState } from 'react';
import { useAuth } from '../hooks/useAuth';

/**
 * The organisation switcher - shown in place of the static org name when the
 * signed-in ACCOUNT belongs to more than one organisation.
 *
 * Picking an org calls /auth/switch-org for fresh single-org tokens and then
 * hard-reloads the app (see AuthContext.switchOrg) so nothing cached from the
 * previous organisation survives. Until the reload lands, the control shows
 * "Switching..." and refuses further clicks.
 */
export function OrgSwitcher({ tone = 'panel' }: { tone?: 'panel' | 'dash' | 'bar' } = {}) {
  const dash = tone === 'dash';
  const bar = tone === 'bar';
  const { user, memberships, switchOrg } = useAuth();
  const [open, setOpen] = useState(false);
  const [switching, setSwitching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Click-away closes the menu.
  useEffect(() => {
    if (!open) return;
    function onPointerDown(event: PointerEvent) {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    }
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open]);

  const currentOrgId = user?.organisation.id;

  async function choose(orgId: string) {
    if (orgId === currentOrgId || switching) {
      setOpen(false);
      return;
    }
    setSwitching(true);
    setError(null);
    try {
      await switchOrg(orgId); // navigates away on success
    } catch {
      setSwitching(false);
      setError('Could not switch. Try again.');
    }
  }

  return (
    <div ref={containerRef} className="relative min-w-0">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        disabled={switching}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={dash
          ? 'dash-focus flex min-w-0 items-center gap-2 rounded-xl border border-dash-line bg-dash-raised px-3 py-1.5 text-left transition hover:border-dash-ink/30 disabled:opacity-60'
          : bar
            ? 'flex min-w-0 max-w-[15rem] items-center gap-2 rounded-lg px-2 py-1 text-left transition hover:bg-slate-100 disabled:opacity-60'
            : 'flex w-full min-w-0 items-center gap-1 rounded-md text-left hover:bg-slate-100 disabled:opacity-60'}
        title="Switch organisation"
      >
        <span className="min-w-0">
          <span className={`block truncate text-sm font-semibold ${dash ? 'text-dash-ink' : 'text-slate-900'}`}>
            {switching ? 'Switching...' : (user?.organisation.name ?? 'Task portal')}
          </span>
          <span className={`block text-xs ${dash ? 'text-dash-muted' : 'text-slate-500'}`}>
            {dash || bar ? (user?.role.name ?? 'Switch organisation') : 'Task portal'}
          </span>
        </span>
        <svg
          aria-hidden="true"
          viewBox="0 0 20 20"
          className={`h-4 w-4 shrink-0 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
        >
          <path d="M6 8l4 4 4-4" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {error && <p className="mt-0.5 text-xs text-red-600">{error}</p>}

      {open && (
        <ul
          role="listbox"
          aria-label="Your organisations"
          className={`absolute left-0 top-full z-30 mt-1 w-60 rounded-lg border py-1 shadow-lg ${dash ? 'border-dash-line bg-dash-raised text-dash-ink' : 'border-slate-200 bg-white'}`}
        >
          {memberships.map((m) => {
            const isCurrent = m.orgId === currentOrgId;
            return (
              <li key={m.orgId}>
                <button
                  type="button"
                  role="option"
                  aria-selected={isCurrent}
                  onClick={() => void choose(m.orgId)}
                  className={[
                    'flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm',
                    isCurrent
                      ? (dash ? 'cursor-default font-semibold text-dash-ink' : 'cursor-default font-semibold text-slate-900')
                      : (dash ? 'text-dash-ink hover:bg-dash-base' : 'text-slate-700 hover:bg-slate-50'),
                  ].join(' ')}
                >
                  <span className="min-w-0">
                    <span className="block truncate">{m.orgName}</span>
                    <span className={`block text-xs font-normal ${dash ? 'text-dash-muted' : 'text-slate-500'}`}>{m.roleName}</span>
                  </span>
                  {isCurrent && (
                    <svg
                      aria-hidden="true"
                      viewBox="0 0 20 20"
                      className="h-4 w-4 shrink-0 text-brand-600"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path d="M4 10.5l4 4 8-9" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
